<template>
  <div>
    <LocationsSelector
      :show-cities="true"
      :show-counties="true"
      :selected-locations="notification.formatted.params.locations"
      :read-only="readOnly"
      @change-locations="handleChangeLocations"
    ></LocationsSelector>

    <v-row>
      <v-col cols="6">
        <v-radio-group
          :value="notification.formatted.params.pulseTrigger"
          label="Pulse change triggers"
          hide-details
          class="mt-0 pt-0 pb-3"
          :disabled="readOnly"
          @change="handleChangePulseTrigger"
        >
          <v-radio value="below-current">
            <template v-slot:label>
              <div>{{ pulseTriggers[0].text }}</div>
            </template>
          </v-radio>
          <v-radio value="score-drop">
            <template v-slot:label>
              <div>
                <div class="pull-left drop-score-text">
                  {{ pulseTriggers[1].text }}
                </div>
                <div class="pull-left">
                  <v-text-field
                    v-show="selectedPulseTrigger === 'score-drop'"
                    v-model="selectedScoreDrop"
                    type="number"
                    :min="pulseTriggerDropMin"
                    :max="pulseTriggerDropMax"
                    height="12"
                    hide-details
                    class="pt-0 mt-0 ml-3 score-textbox"
                    @blur="handleBlurScoreDrop"
                    @keyup="handleKeyUpScoreDrop"
                  ></v-text-field>
                </div>
                <div class="clearfix"></div>
              </div>
            </template>
          </v-radio>
          <v-radio value="below-moving">
            <template v-slot:label>
              <div>{{ pulseTriggers[2].text }}</div>
            </template>
          </v-radio>
        </v-radio-group>
      </v-col>
      <v-col class="grey--text">
        <div v-if="!selectedPulseTrigger">
          Pulse triggers allow you to be alerted of significant drops in
          stability so that you can monitor events proactively.
        </div>
        <div v-if="selectedPulseTrigger">
          <p class="application-heading">
            {{ selectedPulseTriggerName }}
          </p>
          <div
            v-html="selectedPulseTriggerDescription"
            class="description-text"
          ></div>
        </div>
        <div class="mt-2">
          <a @click="handleClickLearnMore">Learn more</a>
        </div>
      </v-col>
    </v-row>

    <ConfirmationPopup
      :show="learnMoreShow"
      :text="learnMoreShowText"
      :buttonCancel="learnMoreBtnCancel"
      :max-width="800"
    ></ConfirmationPopup>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";

import { MODULE_PREFIX_NOTIFICATIONS } from "@/store";
import { GET_NOTIFICATION } from "@/store/actions.type";
import { mapGetters } from "vuex";
import {
  NOTIFICATION_PULSE_TRIGGER_DROP_MIN,
  NOTIFICATION_PULSE_TRIGGER_DROP_MAX,
} from "@/common/constants/notifications";

export default {
  name: "NotificationsTripwiresPulse",
  components: {
    LocationsSelector: () => import("@/components/common/LocationsSelector"),
    ConfirmationPopup: () => import("@/components/common/ConfirmationPopup"),
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const pulseTriggers = [
      {
        text: "Fixed value with no timeframe",
        value: "below-current",
        description: `
        You will be notified if the Pulse score drops below the value at any point in the future. The value is fixed at the average on the day the alert is set.<br/>
        Good for:
        <ul>
          <li>Long-term monitoring of a location – including picking up gradual deterioration in stability levels</li>
          <li>Identifying specific thresholds for organizational protocols</li>
        </ul>
        `,
      },
      {
        text: "Chosen value in 24 hours",
        value: "score-drop",
        description: `
        You will be notified if the Pulse score drops by your selected value within a 24-hour timeframe. Your chosen value can be calibrated to the level of volatility in your selected location.
        Good for:
        <ul>
          <li>Granular monitoring of a variety of areas</li>
          <li>Identifying large swings in stability even in regions with significant volatility</li>
          <li>Identifying unexpected drops in stability in regions that are historically stable</li>
        </ul>
        `,
      },
      {
        text: "Outside average",
        value: "below-moving",
        description: `
        You will be notified if the Pulse score drops below the 60-day moving average for your selected region, indicating a sudden, sharp drop in stability beyond the normal bounds. 
        Good for:
        <ul>
        <li>Monitoring areas with upcoming events, such as elections, with expected unrest or potential for shifts in stability</li>
        <li>To cover a large area of responsibility and identify any regions/countries with sudden and major drops in stability</li>
        <li>As a significant indicator of change in the situation in an area with an uncertain outlook</li>
        </ul>
        `,
      },
    ];
    const scoreDropDefault = 5;
    const imgSrc = require("@/assets/images/miscellaneous/pulse_gauge_explainer.png");
    const learnMoreExampleImg = `<img src="${imgSrc}" />`;
    const learnMoreShowText = `
      <p>${learnMoreExampleImg}</p>
      <h1 class="mb-5">More About Pulse Triggers</h1>
      <dl>
        <dt class="application-heading" style="text-decoration: underline;">Fixed value with no timeframe</dt>
          <dd>
            <p>In this case for Hungary, you will receive an alert when the Pulse score drops below 42 - the low point of today's normal range (which is 2 standard deviations above and below the 60-day mean). This threshold is fixed and will not change over time - even though the normal range updates each day.</p>
            <p>Setting the specific value to today's range will alert you to meaningful negative changes in stability over time, even if these are gradual and without any sudden drops.</p>
          </dd>
        <dt class="application-heading" style="text-decoration: underline;">Chosen value in 24 hours</dt>
          <dd>
            <p>In this case for Hungary, where a 5-point drop has been specified, you will receive an alert when the Pulse score drops by 5 points or more within 24 hours. For example, you will receive an email alert when the score drops from 52 to 47, or from 61 to 53 in a 24-hour period. You will not receive an alert if the score drops by 5 points over a longer period.</p>
            <p>The specified drop is fixed (in this case to 5 points), but the score from which the drop is calculated will change and could be above or below today's level</p>
            <p>Setting a specified score drop allows you to be notified of daily Pulse changes that are significant to your organisation, both within and outside the location's normal (average) range.</p>
          </dd>
        <dt class="application-heading" style="text-decoration: underline;">Outside average</dt>
          <dd>
            <p>You will receive an alert when the Pulse score falls below a future day's normal range (which is 2 standard deviations above and below the 60-day mean). In this case for Hungary, today's normal range is 27-65: you would only receive an alert if the score falls to 26 or below.</p>
            <p>This threshold will change over time and the low threshold could be above or below the current level. In this case, if the normal range for Hungary on a given day has changed to 23-59, you would only receive an alert if the score falls below 23.</p>
            <p>Setting an alert for below moving normal range will notify you of sudden, sharp drops in stability, where the Pulse score goes beyond its normal bounds. However, it will not alert you to a slow decline in Pulse score that would indicate a steady deterioration in conditions.</p>
          </dd>
      </dl>
    `;

    return {
      pulseTriggers: pulseTriggers,
      selectedPulseTrigger: null,
      scoreDropDefault: scoreDropDefault,
      selectedScoreDrop: scoreDropDefault,
      learnMoreShow: false,
      learnMoreShowText: learnMoreShowText,
    };
  },
  computed: {
    ...mapGetters({
      notification: MODULE_PREFIX_NOTIFICATIONS + "notification",
    }),
    pulseTriggerDropMin() {
      return NOTIFICATION_PULSE_TRIGGER_DROP_MIN;
    },
    pulseTriggerDropMax() {
      return NOTIFICATION_PULSE_TRIGGER_DROP_MAX;
    },
    selectedPulseTriggerName() {
      if (this.selectedPulseTrigger) {
        const pulseTrigger = lodashFind(this.pulseTriggers, [
          "value",
          this.selectedPulseTrigger,
        ]);
        if (pulseTrigger) {
          return pulseTrigger.text;
        }
      }
      return null;
    },
    selectedPulseTriggerDescription() {
      if (this.selectedPulseTrigger) {
        const pulseTrigger = lodashFind(this.pulseTriggers, [
          "value",
          this.selectedPulseTrigger,
        ]);
        if (pulseTrigger) {
          return pulseTrigger.description;
        }
      }
      return null;
    },
    learnMoreBtnCancel() {
      return {
        text: "Close",
        onClickFnc: async () => {
          this.learnMoreShow = false;
        },
      };
    },
  },
  methods: {
    handleChangeLocations(locations) {
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.params = notification.formatted.params || {};
      notification.formatted.params.locations = locations;
      this.update(notification);
    },
    handleChangePulseTrigger(value) {
      this.selectedPulseTrigger = value;
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.params = notification.formatted.params || {};
      notification.formatted.params.pulseTrigger = value;
      notification.formatted.params.pulseScoreDrop = this.scoreDropDefault;
      if (notification.formatted.params.pulseTrigger === "score-drop") {
        notification.formatted.params.pulseScoreDrop = this.selectedScoreDrop;
      }
      this.update(notification);
    },
    handleKeyUpScoreDrop() {
      this.scoreDropUpdate();
    },
    handleBlurScoreDrop() {
      if (!this.selectedScoreDrop) {
        this.selectedScoreDrop = this.scoreDropDefault;
      }
      this.scoreDropUpdate();
    },
    scoreDropUpdate() {
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.params = notification.formatted.params || {};
      if (notification.formatted.params.pulseTrigger === "score-drop") {
        notification.formatted.params.pulseScoreDrop = this.selectedScoreDrop;
      }
      this.update(notification);
    },
    update(notification) {
      this.$store.dispatch(
        MODULE_PREFIX_NOTIFICATIONS + GET_NOTIFICATION,
        notification
      );
    },
    handleClickLearnMore() {
      this.learnMoreShow = true;
    },
  },
  watch: {
    notification: {
      handler() {
        if (lodashGet(this.notification, "formatted.params.pulseTrigger")) {
          this.selectedPulseTrigger = this.notification.formatted.params.pulseTrigger;
          if (
            this.notification.formatted.params.pulseTrigger === "score-drop" &&
            lodashGet(this.notification, "formatted.params.pulseScoreDrop")
          ) {
            this.selectedScoreDrop = this.notification.formatted.params.pulseScoreDrop;
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
.score-textbox {
  width: 50px;
  position: relative;
  top: 3px;
}
.drop-score-text {
  width: 140px;
}
.description-text {
  font-size: 0.75rem;
}
</style>
